import format from 'date-fns/format';
import isSameDay from 'date-fns/isSameDay';
import { i18n } from '../../../i18n-handler/handler/i18n';
import { IMissionGroupExtra } from '../../../interfaces/missions/IMissionGroupExtra';
import { IAcceptMissionGroupConfirmInfos } from '../../interfaces/IAcceptMissionGroupConfirmInfos';
import { getHourFormatFromMinutes } from '../../utils/format.utils';
import { MissionGroupStatusEnum } from '../../../interfaces/misc/MissionGroupStatusEnum';

let lang = 'fr';

export type MissionGroupStatusClass = 'ignored' | 'cancelled' | 'confirmed' | 'waiting' | 'available' | '';

/**
 * AVAILABLE => disponible
 * WAITING => en attente de réponse
 * CONFIRMED => confirmée
 * CANCELLED => annulée
 * IGNORED => ignorée
 */
function getMissionStatusAndClass(missionGroup: IMissionGroupExtra): {
    status: string;
    class: MissionGroupStatusClass;
} {
    let _status = '';
    let _class: MissionGroupStatusClass = '';

    if (missionGroup._status === MissionGroupStatusEnum.AVAILABLE) {
        _status = `•\u00A0${i18n.t('mission.status.available', { lng: lang })}`;
        _class = 'available';
    } else if (missionGroup._status === MissionGroupStatusEnum.WAITING) {
        _status = `•\u00A0${i18n.t('mission.status.waiting', { lng: lang })}`;
        _class = 'waiting';
    } else if (missionGroup._status === MissionGroupStatusEnum.CONFIRMED) {
        _status = `•\u00A0${i18n.t('mission.status.confirmed', { lng: lang })}`;
        _class = 'confirmed';
    } else if (missionGroup._status === MissionGroupStatusEnum.CANCELLED) {
        _status = `•\u00A0${i18n.t('mission.status.cancelled', { lng: lang })}`;
        _class = 'cancelled';
    } else if (missionGroup._status === MissionGroupStatusEnum.IGNORED) {
        _status = `•\u00A0${i18n.t('mission.status.ignored', { lng: lang })}`;
        _class = 'ignored';
    } else {
        _status = '-';
    }

    return { status: _status, class: _class };
}

function getJobText(missionGroup: IMissionGroupExtra): string | undefined {
    if (missionGroup && missionGroup.jobs && missionGroup.jobs.length >= 1) {
        if (missionGroup.jobs[0].category && missionGroup.jobs[0].category.name) {
            return `${missionGroup.jobs[0].category.name} - ${missionGroup.jobs[0].name}`;
        }
        return `${missionGroup.jobs[0].name}`;
    }
}

function getPriceHourText(missionGroup: IMissionGroupExtra): string | undefined {
    if (missionGroup && missionGroup.amounts && missionGroup.amounts.missionPriceHour) {
        return `${missionGroup.amounts.missionPriceHour.amount_formatted} / H`;
    }
}

function getPriceHourFullText(missionGroup: IMissionGroupExtra): string | undefined {
    if (missionGroup && missionGroup.amounts && missionGroup.amounts.missionPriceHour) {
        return `${missionGroup.amounts.missionPriceHour.amount_formatted} ${i18n.t('mission.details.by_hour', {
            lng: lang,
        })}`;
    }
}

function getHoursText(missionGroup: IMissionGroupExtra): string {
    return getHourFormatFromMinutes(missionGroup.totalOfMinutes);
}

function getDatesText(missionGroup: IMissionGroupExtra): string | undefined {
    if (missionGroup?.workWindows !== undefined && missionGroup.workWindows.length > 0) {
        const dateList = missionGroup.workWindows
            .map((workWindow) => workWindow.startTime)
            .filter((date, i, arr) => arr.findIndex((value) => isSameDay(value, date)) === i);
        const startDayDate = new Date(missionGroup.workWindows[0].startTime);
        const endDayDate = new Date(missionGroup.workWindows[missionGroup.workWindows.length - 1].endTime);
        if (isSameDay(startDayDate, endDayDate)) {
            return `${format(startDayDate, 'dd/MM/yyyy')}`;
        } else {
            return `${format(startDayDate, 'dd/MM')} - ${format(endDayDate, 'dd/MM/yyyy')} (${dateList.length} ${i18n.t(
                'mission.details.days',
                { lng: lang }
            )})`;
        }
    }
    return '';
}

function getLanguagesText(missionGroup: IMissionGroupExtra): string | undefined {
    let languagesText = '';
    if (missionGroup && missionGroup.languages && missionGroup.languages.length >= 1) {
        languagesText = missionGroup.languages.map((language) => language.name).join(', ');
    } else {
        languagesText = '-';
    }
    return languagesText;
}

function getSoftwaresText(missionGroup: IMissionGroupExtra): string | undefined {
    let softwaresText = '';
    if (missionGroup && missionGroup.softwares && missionGroup.softwares.length >= 1) {
        softwaresText = missionGroup.softwares.map((software) => software.name).join(', ');
    } else {
        softwaresText = '-';
    }
    return softwaresText;
}

function getHasSoftware(missionGroup: IMissionGroupExtra): boolean {
    let hasSoftware = false;
    missionGroup.softwares && missionGroup.softwares.length > 0 ? (hasSoftware = true) : '';
    return hasSoftware;
}

function getHasLanguage(missionGroup: IMissionGroupExtra): boolean {
    let hasLanguage = false;
    missionGroup.languages && missionGroup.languages.length > 0 ? (hasLanguage = true) : '';
    return hasLanguage;
}

function getHasDetails(missionGroup: IMissionGroupExtra): boolean {
    let hasDetails = false;
    missionGroup.details && missionGroup.details.length > 0 ? (hasDetails = true) : '';
    return hasDetails;
}

/**
 * Details pour l'extra avant d'accepter définitivement une mission.
 * @param missionGroup
 */
function getAcceptMissionConfirmInfos(missionGroup: IMissionGroupExtra): IAcceptMissionGroupConfirmInfos {
    const proName = missionGroup.pro.displayName;
    const dates = getDatesText(missionGroup);
    const address = missionGroup.address.formatted_address;
    const softwares = missionGroup.softwares ? missionGroup.softwares.map((v) => v.name).join('-') : '';
    const languages = missionGroup.languages ? missionGroup.languages.map((v) => v.name).join('-') : '';
    const details = missionGroup.details;
    const result: IAcceptMissionGroupConfirmInfos = {
        confirm: {
            title: `${i18n.t('mission.accept-mission-group-confirm-infos.confirm.title', { lng: lang })}`,
            check: `${i18n.t('mission.accept-mission-group-confirm-infos.confirm.check', { lng: lang })}`,
            engage: `${i18n.t('mission.accept-mission-group-confirm-infos.confirm.engage', { lng: lang })}`,
        },
        infos: {
            name: {
                key: `${i18n.t('mission.accept-mission-group-confirm-infos.summary.pro', { lng: lang })}`,
                value: `${proName}`,
            },
            dates: {
                key: `${i18n.t('mission.accept-mission-group-confirm-infos.summary.dates', { lng: lang })}`,
                value: `${dates}`,
            },
            address: {
                key: `${i18n.t('mission.accept-mission-group-confirm-infos.summary.address', { lng: lang })}`,
                value: `${address}`,
            },
        },
    };
    if (softwares) {
        result.infos.softwares = {
            key: `${i18n.t('mission.accept-mission-group-confirm-infos.summary.softwares', { lng: lang })}`,
            value: `${softwares}`,
        };
    }
    if (languages) {
        result.infos.languages = {
            key: `${i18n.t('mission.accept-mission-group-confirm-infos.summary.languages', { lng: lang })}`,
            value: `${languages}`,
        };
    }
    if (details) {
        result.infos.details = {
            key: `${i18n.t('mission.accept-mission-group-confirm-infos.summary.details', { lng: lang })}`,
            value: `${details}`,
        };
    }
    return result;
}

/**
 * @description Returns the percentage of the difference between the base price and the price offered by the pro
 */
function getPercentPriceHour(missionGroup: IMissionGroupExtra): {
    isMoreThanDefaultPrice: boolean;
    percentOfDifferenceBetweenPrice: number;
} {
    return {
        isMoreThanDefaultPrice: (missionGroup.pricePerHour * 100) / missionGroup.jobs[0].prices.base.amount > 100,
        percentOfDifferenceBetweenPrice:
            ((missionGroup.pricePerHour - missionGroup.jobs[0].prices.base.amount) /
                missionGroup.jobs[0].prices.base.amount) *
            100,
    };
}

export type ExtraFormatReturn = {
    getHoursText: (missionGroup: IMissionGroupExtra) => string;
    getHasDetails: (missionGroup: IMissionGroupExtra) => boolean;
    getHasLanguage: (missionGroup: IMissionGroupExtra) => boolean;
    getHasSoftware: (missionGroup: IMissionGroupExtra) => boolean;
    getJobText: (missionGroup: IMissionGroupExtra) => string | undefined;
    getDatesText: (missionGroup: IMissionGroupExtra) => string | undefined;
    getPriceHourText: (missionGroup: IMissionGroupExtra) => string | undefined;
    getPriceHourFullText: (missionGroup: IMissionGroupExtra) => string | undefined;
    getLanguagesText: (missionGroup: IMissionGroupExtra) => string | undefined;
    getSoftwaresText: (missionGroup: IMissionGroupExtra) => string | undefined;
    getAcceptMissionConfirmInfos: (missionGroup: IMissionGroupExtra) => IAcceptMissionGroupConfirmInfos;
    getPercentPriceHour: (missionGroup: IMissionGroupExtra) => {
        isMoreThanDefaultPrice: boolean;
        percentOfDifferenceBetweenPrice: number;
    };
    getMissionStatusAndClass: (missionGroup: IMissionGroupExtra) => {
        status: string;
        class: MissionGroupStatusClass;
    };
};

export const extraFormat = (_lang = 'fr'): ExtraFormatReturn => {
    lang = _lang;

    return {
        getJobText,
        getHoursText,
        getDatesText,
        getHasDetails,
        getHasSoftware,
        getHasLanguage,
        getPriceHourText,
        getPriceHourFullText,
        getLanguagesText,
        getSoftwaresText,
        getPercentPriceHour,
        getMissionStatusAndClass,
        getAcceptMissionConfirmInfos,
    };
};
